import { useCallback, useEffect, useState } from 'react';
import { Package, ClipboardList, Clock3, CheckCircle2, CircleDashed, Truck, Bell } from 'lucide-react';
import { getMyOrders } from '../api';
import { useAppStore } from '../store';
import type { Order } from '../types';
import { GlassCard, SectionTitle, StatusPill } from '../components/Glass';
import { useHaptic } from '../hooks/useHaptic';
import { formatOrderDimensionLines } from '../lib/dimensions';
import { readOrdersSnapshot, writeOrdersSnapshot, snapshotFromOrders } from '../ordersSnapshotStorage';

const STATUS_STYLE: Record<string, { label: string; bg: string; text: string }> = {
  draft:         { label: 'Черновик',       bg: 'rgba(255,255,255,0.06)', text: 'var(--text-2)' },
  pending:       { label: 'Ожидает',        bg: 'rgba(246,173,85,0.15)',  text: '#F6AD55' },
  confirmed:     { label: 'Подтверждён',  bg: 'rgba(99,179,237,0.15)', text: 'var(--cyan-bright)' },
  in_production: { label: 'В производстве', bg: 'rgba(159,122,234,0.15)', text: '#B794F4' },
  ready:         { label: 'Готов',          bg: 'rgba(104,211,145,0.15)', text: '#68D391' },
  delivered:     { label: 'Доставлен',     bg: 'rgba(72,187,120,0.15)',  text: '#48BB78' },
  cancelled:     { label: 'Отменён',       bg: 'rgba(252,129,74,0.15)',  text: '#FC814A' },
};

const STATUS_STAGE: Record<string, number> = {
  draft: 0,
  pending: 1,
  confirmed: 2,
  in_production: 3,
  ready: 4,
  delivered: 5,
  cancelled: 0,
};

const PIPELINE = ['Заявка', 'Подтверждение', 'Производство', 'Готовность', 'Доставка'];

function formatOrderItemDetails(snapshot: Record<string, unknown> | undefined): string[] {
  if (!snapshot) return [];
  const lines: string[] = [];
  const protocol = typeof snapshot.protocol === 'string' ? snapshot.protocol : '';
  const color = typeof snapshot.color === 'string' ? snapshot.color : '';
  const opening = typeof snapshot.opening_type === 'string' ? snapshot.opening_type : '';
  if (protocol) lines.push(`Протокол: ${protocol}`);
  if (color) lines.push(`Цвет: ${color}`);
  if (opening === 'center') lines.push('Открывание: к центру');
  if (opening === 'left') lines.push('Открывание: влево');
  if (opening === 'right') lines.push('Открывание: вправо');
  if (snapshot.v2_price_on_request) lines.push('Цена: по запросу');
  lines.push(
    ...formatOrderDimensionLines({
      length_m: typeof snapshot.length_m === 'number' ? snapshot.length_m : null,
      width_m: typeof snapshot.width_m === 'number' ? snapshot.width_m : null,
      projection_m: typeof snapshot.projection_m === 'number' ? snapshot.projection_m : null,
      custom_params: snapshot as Record<string, unknown>,
    }),
  );
  return lines;
}

function readOrderBreakdown(snapshot: Record<string, unknown> | undefined): { base: number | null; surcharges: Array<{ label: string; amount: number }> } {
  if (!snapshot) return { base: null, surcharges: [] };
  const baseRaw = Number((snapshot as any).v2_base_price);
  const base = Number.isFinite(baseRaw) ? baseRaw : null;
  const src = Array.isArray((snapshot as any).v2_surcharges) ? (snapshot as any).v2_surcharges : [];
  const surcharges = src
    .map((s: any) => ({ label: String(s?.label ?? ''), amount: Number(s?.amount ?? 0) }))
    .filter((s: { label: string; amount: number }) => s.label && Number.isFinite(s.amount) && s.amount > 0);
  return { base, surcharges };
}

export default function OrdersPage() {
  const { token } = useAppStore();
  const haptic = useHaptic();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [activityIds, setActivityIds] = useState<Set<number>>(() => new Set());

  const applyOrdersAndDetectActivity = useCallback((list: Order[]) => {
    setOrders(list);
    const prev = readOrdersSnapshot();
    const hadBaseline = Object.keys(prev).length > 0;
    if (!hadBaseline) {
      writeOrdersSnapshot(snapshotFromOrders(list));
      setActivityIds(new Set());
      return;
    }
    const next = new Set<number>();
    for (const o of list) {
      const p = prev[o.id];
      if (!p) next.add(o.id);
      else if (p.status !== o.status || p.updated_at !== o.updated_at) next.add(o.id);
    }
    setActivityIds(next);
  }, []);

  const loadOrders = useCallback(
    async (opts?: { soft?: boolean }) => {
      if (!token) return;
      if (!opts?.soft) setLoading(true);
      try {
        const list = await getMyOrders();
        applyOrdersAndDetectActivity(list);
      } catch (e) {
        console.error(e);
      } finally {
        if (!opts?.soft) setLoading(false);
      }
    },
    [token, applyOrdersAndDetectActivity],
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    void loadOrders();
  }, [token, loadOrders]);

  useEffect(() => {
    if (!token) return;
    const id = window.setInterval(() => void loadOrders({ soft: true }), 42000);
    return () => window.clearInterval(id);
  }, [token, loadOrders]);

  const dismissActivityBanner = () => {
    haptic.selection();
    writeOrdersSnapshot(snapshotFromOrders(orders));
    setActivityIds(new Set());
  };

  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-5 px-8 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid var(--border-glass)' }}>
          <ClipboardList size={36} color="var(--cyan)" />
        </div>
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-1)' }}>Заказы недоступны</h2>
          <p className="text-sm" style={{ color: 'var(--text-2)' }}>Войдите через Telegram-бота</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--cyan)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="header-glass px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-1)' }}>Мои заказы</h1>
          <div className="flex items-center gap-2">
            {activityIds.size > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1"
                style={{ background: 'rgba(129,199,132,0.2)', color: '#B7F5CB', border: '1px solid rgba(129,199,132,0.45)' }}
              >
                <Bell size={11} aria-hidden />
                Есть обновления
              </span>
            )}
            {orders.length > 0 && <StatusPill>{orders.length} проектов</StatusPill>}
          </div>
        </div>
      </div>

      {activityIds.size > 0 && (
        <div className="px-4 pt-3">
          <div
            className="rounded-2xl px-3 py-2.5 flex items-start justify-between gap-3"
            style={{ background: 'rgba(129,199,132,0.1)', border: '1px solid rgba(129,199,132,0.35)' }}
          >
            <div className="min-w-0">
              <p className="text-xs font-bold" style={{ color: '#E8FFF0' }}>
                Статус заказа обновлён
              </p>
              <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--text-3)' }}>
                Подсвечены карточки ({activityIds.size}). Менеджер мог оставить комментарий — раскройте заказ ниже.
              </p>
            </div>
            <button
              type="button"
              onClick={dismissActivityBanner}
              className="press shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--cyan-bright)', border: '1px solid var(--border-glass)' }}
            >
              Понятно
            </button>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-5 px-8 text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: 'var(--sapphire-card)', border: '1px solid var(--border-subtle)' }}>
            <Package size={40} color="var(--text-3)" />
          </div>
          <div>
            <p className="font-bold text-lg mb-1" style={{ color: 'var(--text-1)' }}>Пока нет активных проектов</p>
            <p className="text-sm" style={{ color: 'var(--text-2)' }}>Соберите первую персональную конфигурацию в каталоге Onviz</p>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <GlassCard className="p-4">
            <SectionTitle className="mb-2">◈ Статус заказов</SectionTitle>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-subtle)' }}>
                <p style={{ color: 'var(--text-3)' }}>Активные</p>
                <p className="font-bold text-base" style={{ color: 'var(--cyan-bright)' }}>
                  {orders.filter((o) => !['delivered', 'cancelled'].includes(o.status)).length}
                </p>
              </div>
              <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(104,211,145,0.08)', border: '1px solid var(--border-subtle)' }}>
                <p style={{ color: 'var(--text-3)' }}>Выполненные</p>
                <p className="font-bold text-base" style={{ color: '#68D391' }}>
                  {orders.filter((o) => o.status === 'delivered').length}
                </p>
              </div>
            </div>
          </GlassCard>

          {orders.map((order) => {
            const st = STATUS_STYLE[order.status] ?? { label: order.status, bg: 'rgba(255,255,255,0.06)', text: 'var(--text-2)' };
            const isOpen = expanded === order.id;
            const stage = STATUS_STAGE[order.status] ?? 0;
            const hasActivity = activityIds.has(order.id);
            return (
              <GlassCard
                key={order.id}
                className="overflow-hidden"
                style={
                  hasActivity
                    ? { boxShadow: '0 0 0 2px rgba(129,199,132,0.55), 0 8px 28px rgba(129,199,132,0.1)' }
                    : undefined
                }
              >
                <button
                  className="w-full p-4 text-left press"
                  onClick={() => {
                    haptic.selection();
                    const nextOpen = isOpen ? null : order.id;
                    setExpanded(nextOpen);
                    if (nextOpen === order.id && hasActivity) {
                      setActivityIds((prev) => {
                        const n = new Set(prev);
                        n.delete(order.id);
                        return n;
                      });
                      const snap = readOrdersSnapshot();
                      snap[order.id] = { status: order.status, updated_at: order.updated_at };
                      writeOrdersSnapshot(snap);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold" style={{ color: 'var(--text-1)' }}>{order.order_number}</span>
                    <span
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: st.bg, color: st.text }}
                    >
                      {st.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                      {new Date(order.created_at).toLocaleDateString('ru-RU', { day:'numeric', month:'long' })}
                    </span>
                    <div className="text-right">
                      {order.discount_pct > 0 && (
                        <p className="text-xs font-medium" style={{ color: '#68D391' }}>−{order.discount_pct.toFixed(0)}%</p>
                      )}
                      <span className="font-bold text-base" style={{ color: 'var(--cyan-bright)' }}>
                        {order.final_amount.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                  </div>
                  {order.status !== 'cancelled' && (
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{ background: 'var(--cyan)', width: `${Math.max(10, (stage / 5) * 100)}%` }}
                        />
                      </div>
                      <p className="text-[11px] mt-1" style={{ color: 'var(--text-3)' }}>
                        Этап: {PIPELINE[Math.max(0, Math.min(PIPELINE.length - 1, stage - 1))] ?? 'Создан'}
                      </p>
                    </div>
                  )}
                  <p className="text-[11px] mt-1.5" style={{ color: 'var(--text-3)' }}>
                    Обновлено: {new Date(order.updated_at).toLocaleDateString('ru-RU', { day:'numeric', month:'long' })} · {new Date(order.updated_at).toLocaleTimeString('ru-RU', { hour:'2-digit', minute:'2-digit' })}
                  </p>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <div className="grid grid-cols-5 gap-1 mt-3 mb-3">
                      {PIPELINE.map((label, idx) => {
                        const point = idx + 1;
                        const done = stage >= point && order.status !== 'cancelled';
                        const isCurrent = stage === point && order.status !== 'cancelled';
                        const Icon = order.status === 'cancelled' ? CircleDashed : (done ? CheckCircle2 : Clock3);
                        return (
                          <div key={label} className="flex flex-col items-center gap-1 text-center">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center"
                              style={done
                                ? { background: 'rgba(104,211,145,0.18)', color: '#68D391', border: '1px solid rgba(104,211,145,0.35)' }
                                : isCurrent
                                ? { background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                                : { background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }
                              }
                            >
                              <Icon size={12} />
                            </div>
                            <p className="text-[9px] leading-tight" style={{ color: done || isCurrent ? 'var(--text-2)' : 'var(--text-3)' }}>{label}</p>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-[10px] font-semibold uppercase tracking-widest mt-3 mb-2" style={{ color: 'var(--text-3)' }}>Состав</p>
                    <div className="space-y-2">
                      {order.items.map((item) => (
                        <div key={item.id} className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <span className="text-sm leading-tight" style={{ color: 'var(--text-2)' }}>
                              {String(item.product_snapshot?.name ?? '—')}
                            </span>
                            {formatOrderItemDetails(item.product_snapshot as Record<string, unknown>).map((line) => (
                              <p key={`${item.id}-${line}`} className="text-[11px] mt-0.5" style={{ color: 'var(--cyan)' }}>
                                {line}
                              </p>
                            ))}
                            {(() => {
                              const breakdown = readOrderBreakdown(item.product_snapshot as Record<string, unknown>);
                              if (breakdown.base === null && breakdown.surcharges.length === 0) return null;
                              return (
                                <div className="mt-1.5 text-[11px]">
                                  {breakdown.base !== null && (
                                    <p style={{ color: 'var(--text-3)' }}>База: {breakdown.base.toLocaleString('ru-RU')} ₽</p>
                                  )}
                                  {breakdown.surcharges.map((s) => (
                                    <p key={`${item.id}-${s.label}`} style={{ color: 'var(--text-3)' }}>
                                      + {s.label}: {s.amount.toLocaleString('ru-RU')} ₽
                                    </p>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                          <span className="text-sm font-semibold shrink-0" style={{ color: (item.product_snapshot as any)?.v2_price_on_request ? '#F6AD55' : 'var(--text-1)' }}>
                            {(item.product_snapshot as any)?.v2_price_on_request ? 'Цена по запросу' : `${item.total_price.toLocaleString('ru-RU')} ₽`}
                          </span>
                        </div>
                      ))}
                    </div>
                    {order.discount_amount > 0 && (
                      <div className="flex justify-between text-xs mt-2" style={{ color: '#68D391' }}>
                        <span>Скидка</span>
                        <span>−{order.discount_amount.toLocaleString('ru-RU')} ₽</span>
                      </div>
                    )}
                    {!!order.manager_notes && (
                      <div className="mt-3 rounded-xl px-3 py-2.5" style={{ background: 'rgba(159,122,234,0.08)', border: '1px solid var(--border-subtle)' }}>
                        <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Комментарий менеджера</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>{order.manager_notes}</p>
                      </div>
                    )}
                    {!!order.status_history?.length && (
                      <div className="mt-3 rounded-xl px-3 py-2.5" style={{ background: 'rgba(99,179,237,0.06)', border: '1px solid var(--border-subtle)' }}>
                        <p className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-3)' }}>История статусов</p>
                        <div className="space-y-1.5">
                          {order.status_history.slice(-5).reverse().map((ev, idx) => (
                            <div key={`${ev.at}-${idx}`} className="text-xs" style={{ color: 'var(--text-2)' }}>
                              <span style={{ color: 'var(--text-3)' }}>
                                {new Date(ev.at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} {new Date(ev.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {'  '}→ {ev.to}
                              {ev.manager_notes ? ` · ${ev.manager_notes}` : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!!order.delivery_address && (
                      <div className="mt-3 rounded-xl px-3 py-2.5 flex items-start gap-2" style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-subtle)' }}>
                        <Truck size={14} color="var(--cyan)" className="shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Адрес доставки</p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>{order.delivery_address}</p>
                        </div>
                      </div>
                    )}
                    {!!order.notes && (
                      <div className="mt-2 rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
                        <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Контакт для договора</p>
                        <p className="text-xs mt-0.5 whitespace-pre-line" style={{ color: 'var(--text-2)' }}>{order.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
